import { useEffect, useRef, useState, type Ref } from "react";
import { useParams } from "react-router-dom";
import { contractArticles, contractIntro, type ContractDraft, type ContractStatus } from "../../shared/contract";
import { api } from "../api";
import { Banner, messageOf } from "./ui";

type PublicContract = { body: ContractDraft; status: ContractStatus; signature: string | null };

export function ContractSignPage() {
  const { token } = useParams();
  const [page, setPage] = useState<PublicContract | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pad = useRef<SignatureHandle>(null);

  useEffect(() => {
    if (!token) return;
    api<PublicContract>(`/public/contract/${encodeURIComponent(token)}`)
      .then((result) => {
        setPage(result);
        setName(result.status.signer_name || result.body.parties.map((party) => party.name).filter(Boolean).join(" και "));
        if (result.status.state === "signed") setNote("Το συμφωνητικό έχει υπογραφεί.");
      })
      .catch((reason: unknown) => setError(messageOf(reason)));
  }, [token]);

  async function sign() {
    if (!token || !page || page.status.state === "signed" || busy) return;
    const signature = pad.current?.toDataUrl();
    if (!signature) {
      setError("Βάλε την υπογραφή σου στο πλαίσιο.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ signer_name: string }>(`/public/contract/${encodeURIComponent(token)}`, {
        method: "POST",
        body: JSON.stringify({ signer_name: name, signature }),
      });
      setPage({ ...page, status: { ...page.status, state: "signed", signer_name: result.signer_name }, signature });
      setNote("Η υπογραφή καταχωρίστηκε. Ευχαριστούμε.");
    } catch (reason: unknown) {
      setError(messageOf(reason));
    } finally {
      setBusy(false);
    }
  }

  const signed = page?.status.state === "signed";
  return (
    <div className="public contract-sign">
      <p className="meta">Ιδιωτικό συμφωνητικό</p>
      <h1>{page?.body.venue_name || "Συμφωνητικό"}</h1>
      <Banner error={error} note={note} />
      {page ? (
        <article className="panel contract-preview">
          <p>{contractIntro(page.body)}</p>
          {page.body.parties.map((party) => (
            <p key={`${party.name}-${party.email}`}>
              {[party.name, party.id_number && `Α.Δ.Τ. ${party.id_number}`, party.tax_id && `Α.Φ.Μ. ${party.tax_id}`, party.phone && `Τηλ. ${party.phone}`, party.email, [party.street, party.city, party.postal_code].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
            </p>
          ))}
          {contractArticles(page.body).map((article) => (
            <section key={article.heading}>
              <h2>{article.heading}</h2>
              {article.lines.map((line, index) => <p key={`${article.heading}-${index}`}>{line}</p>)}
            </section>
          ))}
          {signed && page.signature ? <img className="signature-image" alt="Υπογραφή" src={page.signature} /> : null}
          {signed ? <p>Υπογραφή: {page.status.signer_name}</p> : (
            <>
              <label>Ονοματεπώνυμο
                <input value={name} onChange={(event) => setName(event.target.value)} />
              </label>
              <p className="meta">Υπογραφή</p>
              <SignaturePad ref={pad} />
              <div className="actions">
                <button type="button" className="secondary" onClick={() => pad.current?.clear()}>Καθαρισμός</button>
                <button type="button" onClick={() => void sign()} disabled={busy}>{busy ? "Καταχώριση…" : "Υπογράφω"}</button>
              </div>
            </>
          )}
        </article>
      ) : null}
    </div>
  );
}

type SignatureHandle = { clear: () => void; toDataUrl: () => string | null };

function SignaturePad({ ref }: { ref: Ref<SignatureHandle> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth || 520;
      const height = 180;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.lineWidth = 2.2;
      context.lineCap = "round";
      context.strokeStyle = "#1d1914";
    };
    resize();
    const point = (event: PointerEvent) => {
      const box = canvas.getBoundingClientRect();
      return { x: event.clientX - box.left, y: event.clientY - box.top };
    };
    const start = (event: PointerEvent) => {
      drawing.current = true;
      dirty.current = true;
      canvas.setPointerCapture(event.pointerId);
      const cursor = point(event);
      context.beginPath();
      context.moveTo(cursor.x, cursor.y);
    };
    const move = (event: PointerEvent) => {
      if (!drawing.current) return;
      const cursor = point(event);
      context.lineTo(cursor.x, cursor.y);
      context.stroke();
    };
    const stop = () => {
      drawing.current = false;
    };
    canvas.addEventListener("pointerdown", start);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", stop);
    canvas.addEventListener("pointerleave", stop);
    return () => {
      canvas.removeEventListener("pointerdown", start);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerup", stop);
      canvas.removeEventListener("pointerleave", stop);
    };
  }, []);

  useEffect(() => {
    if (!ref) return;
    const handle: SignatureHandle = {
      clear: () => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext("2d");
        if (!canvas || !context) return;
        context.clearRect(0, 0, canvas.width, canvas.height);
        dirty.current = false;
      },
      toDataUrl: () => (dirty.current && canvasRef.current ? canvasRef.current.toDataURL("image/png") : null),
    };
    if (typeof ref === "function") ref(handle);
    else ref.current = handle;
  }, [ref]);

  return <canvas className="signature-pad" ref={canvasRef} />;
}
